import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData, graphql } from '@packages/gql/generated/shopping';
import { useConfig } from '@packages/utilities';

const SchemaOrgFragment = graphql(/* GraphQL */ `
  fragment SchemaOrgFragment on Product {
    name
    ean
    media {
      type
      url
    }
    offers {
      currency
      price
      availability
      sku
    }
    price {
      currency
      value
    }
    brand {
      name
      image
    }
    slug
    akl
    sku
    orderNumber
    availability {
      state
    }
    sellingPoints
    longDescription
    ratingForSchema: seoRating(locale: $locale) {
      product {
        avgRating
        reviewCount
      }
    }
  }
`);

export const SchemaOrg = ({
  productData,
}: {
  productData: FragmentType<typeof SchemaOrgFragment>;
}): string => {
  const product = getFragmentData(SchemaOrgFragment, productData);
  const { company, host } = useConfig();
  const {
    offers,
    name,
    media,
    akl,
    brand,
    price,
    ratingForSchema,
    ean,
    longDescription,
    sellingPoints,
  } = product;

  const hostname =
    typeof window !== 'undefined'
      ? `${window?.location?.protocol}//${window?.location?.hostname}`
      : `https://www.${host.domain}`;

  const offersJSON = offers.map(
    (offer) => `
{
  "@type": "Offer",
  "priceCurrency": "${offer.currency}",
  "price": "${offer.price ? (offer.price / 100).toFixed(2) : 0}",
  "availability": "http://schema.org/${offer.availability}",
  "itemCondition": "http://schema.org/NewCondition",
  "itemOffered": {
    "@type": "IndividualProduct",
    "sku": "${offer.sku}"
  },
  "url": "${hostname}/p/${product.slug}/${product.akl}",
  "seller": {
    "@type": "Organization",
    "name": "${company.name}"
  }
}
`,
  );

  let availablilityForSchema = 'InStock';
  switch (product.availability?.state) {
    case 'NOT_AVAILABLE':
      availablilityForSchema = 'OutOfStock';
      break;
    case 'NOT_YET_AVAILABLE':
      availablilityForSchema = 'BackOrder';
      break;
    default:
      availablilityForSchema = 'InStock';
      break;
  }

  let ratingSchema = '';
  if (ratingForSchema?.product && ratingForSchema.product.reviewCount > 0) {
    ratingSchema = `"aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": "${ratingForSchema.product.avgRating}",
            "reviewCount": "${ratingForSchema.product.reviewCount}",
            "bestRating": 5,
            "worstRating": 1
          },`;
  }

  return `{
      "@context": "https://schema.org/",
      "@type": "Product",
      "url": "${hostname}/p/${product.slug}/${product.akl}",
      "gtin13": "${ean}",
      "name": "${name.replace(/"/g, '&quot;')}",
      "image": ${JSON.stringify(media.map((image) => image.url))},
      "description": "${
        // eslint-disable-next-line no-nested-ternary
        longDescription
          ? longDescription.replace(/"/g, '&quot;')
          : sellingPoints.join('') !== ''
          ? sellingPoints.join('. ').replace(/"/g, '&quot;')
          : ''
      }",
      "sku": "${akl}",
      "brand": {
        "@type": "Brand",
        "name": "${brand?.name.replace(/"/g, '&quot;')}",
        "logo": "${brand?.image}"
      },
      ${ratingSchema}
      "offers": [{
        "@type": "Offer",
        "priceCurrency": "${price.currency}",
        "price": "${(price.value / 100).toFixed(2)}",
        "availability": "http://schema.org/${availablilityForSchema}",
        "itemCondition": "http://schema.org/NewCondition",
        "url": "${hostname}/p/${product.slug}/${product?.akl}",
        "itemOffered": {
          "@type": "IndividualProduct",
          "sku": "${product.orderNumber}-${product.sku.split('-')[1]}"
        },
        "seller": {
          "@type": "Organization",
          "name": "${company.name}"
        }
      }
      ${offersJSON.length > 0 ? `,${offersJSON.join(',')}` : ''}
    ]
    }
  `;
};
